import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, useBeforeUnload } from "react-router-dom";
import useNavigationBlocker from "@hooks/useNavigationBlocker";

export function useUnsavedChanges(hasChanges: boolean, onDiscard?: () => void) {
    const navigate = useNavigate();
    const location = useLocation();

    // 변경 감지 모달 상태
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);

    // 1. 브라우저 창 닫기/새로고침 방지
    useBeforeUnload(
        useCallback(
            (event) => {
                if (hasChanges) {
                    event.preventDefault();
                    event.returnValue = "";
                }
            },
            [hasChanges]
        )
    );

    // 2. 브라우저 뒤로가기 방지 (useNavigationBlocker 사용)
    useNavigationBlocker(hasChanges, () => {
        setShowNavigationModal(true);
    });

    // 3. Link 클릭 차단 (사이드바 등)
    useEffect(() => {
        if (!hasChanges) return;

        const handleLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a[href]');

            if (link && link instanceof HTMLAnchorElement) {
                const href = link.getAttribute('href');
                if (href && !href.startsWith('#') && !href.startsWith('javascript') && href !== location.pathname) {
                    e.preventDefault();
                    e.stopPropagation();
                    setPendingPath(href);
                    setShowNavigationModal(true);
                }
            }
        };

        document.addEventListener('click', handleLinkClick, true);
        return () => document.removeEventListener('click', handleLinkClick, true);
    }, [hasChanges, location.pathname]);

    // 저장 후 네비게이션 진행
    const proceedNavigation = useCallback(() => {
        setShowNavigationModal(false);
        if (pendingPath) {
            setTimeout(() => {
                window.location.href = pendingPath;
            }, 100);
        } else {
            window.history.back();
        }
        setPendingPath(null);
    }, [navigate, pendingPath]);

    // 저장하지 않고 페이지 이동 (변경사항 버림)
    const handleCancelNavigation = useCallback(() => {
        if (onDiscard) onDiscard(); // 변경사항 해제 (부모 상태 업데이트)
        setShowNavigationModal(false);

        // 상태 업데이트 반영을 위해 setTimeout 사용
        setTimeout(() => {
            if (pendingPath) {
                navigate(pendingPath);
            } else {
                navigate(-1);
            }
        }, 0);

        setPendingPath(null);
    }, [navigate, pendingPath, onDiscard]);

    // 모달 닫기 (페이지 머무름)
    const closeModal = useCallback(() => {
        setShowNavigationModal(false);
        setPendingPath(null);
    }, []);

    return {
        // 상태
        showNavigationModal,
        pendingPath,

        // 핸들러
        handleCancelNavigation,
        proceedNavigation,
        closeModal,
    };
}